import clsx from 'clsx'
import { useSession } from 'next-auth/react'
import React, { useEffect, useRef, useState } from 'react'

import { Button } from '@wartek-id/button'
import {
  Dialog,
  DialogCloseButton,
  DialogContent,
  DialogFooter,
  DialogOverlay,
} from '@wartek-id/dialog'
import { Icon } from '@wartek-id/icon'
import { Popover, PopoverContent, PopoverTrigger } from '@wartek-id/popover'
import { Text } from '@wartek-id/text'
import { useToast } from '@wartek-id/toast'

import { RegionDetail } from 'api/region/useAPIRegion'
import {
  RequestBodyImpersonateGroup,
  useAPIGetGroups,
  useAPIImpersonateGroup,
} from 'api/userManagement/useAPIUserManagement'

import { useCreateProgramDraft } from 'app/KS/KSTraining/pages/Create/store'
import { DEFAULT_ERROR_MESSAGE } from 'app/resources'

import {
  BCKS_ADMIN,
  BCKS_GTK,
  BCKS_REGION_PREFIX,
  BCKS_UPT_PREFIX,
  ROLE_DETAILS,
} from 'configs/roles'

import { useAuth } from 'utils/hooks/useAuth'
import { useRegionContext } from 'utils/hooks/useRegion'
import { useRoles } from 'utils/hooks/useRoles'
import { getRegionType } from 'utils/region'

import CustomBadge from 'components/CustomBadgeNew/CustomBadge'
import InputSearch from 'components/InputSearch'

import { RegionType } from 'types/region-type'

interface RegionOption extends RegionDetail {
  parent?: RegionDetail
}

const REGION_EMPTY: RegionDetail = {
  id: null,
  name: null,
  type: null,
}

const MAP_ROLE_SWITCH: Record<string, RequestBodyImpersonateGroup['group']> = {
  [BCKS_REGION_PREFIX]: 'DINAS',
  [BCKS_UPT_PREFIX]: 'UPT',
  [BCKS_GTK]: 'GTK',
  [BCKS_ADMIN]: 'ADMIN',
}

const RoleAndRegionDetails = () => {
  const toast = useToast()
  const {
    activeRole,
    activeRoleName,
    canChangeRegion,
    isSuperAdminUser,
    hasConflictingRoles,
  } = useRoles()
  const {
    provinceData,
    regionData,
    regionDetail,
    setSelectedRegion: applySelectedRegion,
  } = useRegionContext()
  const { data: session } = useSession()
  const user = session?.user ?? {}
  const { bcksUserSession, setBcksUserSession } = useAuth()
  const refInputSelectedRegion = useRef(null)
  const { program, reset: resetProgramDraft } = useRef(
    useCreateProgramDraft()
  ).current

  const { refetch: getGroups } = useAPIGetGroups(
    {
      email: user.email,
      limit: 100,
    },
    {
      enabled: !!user?.email,
      onSuccess(data) {
        setBcksUserSession({
          ...bcksUserSession,
          user: {
            ...bcksUserSession.user,
            groups: data.data,
          },
        })
      },
    }
  )
  const {
    mutateAsync: impersonateGroup,
    isLoading: isLoadingImpersonateGroup,
  } = useAPIImpersonateGroup()

  const DEFAULT_ROLE = activeRole?.startsWith(BCKS_REGION_PREFIX)
    ? BCKS_REGION_PREFIX
    : activeRole?.startsWith(BCKS_UPT_PREFIX)
      ? BCKS_UPT_PREFIX
      : activeRole
  const [isOpenDialogChangeRole, setIsOpenDialogChangeRole] = useState(
    !regionDetail.id && canChangeRegion
  )
  const [searchRegion, setSearchRegion] = useState('')
  const [selectedRole, setSelectedRole] = useState(DEFAULT_ROLE)
  const [selectedRegion, setSelectedRegion] = useState(regionDetail)

  const filterRegion = (region: RegionDetail) => {
    const searched = region.name
      .toLowerCase()
      .includes(searchRegion.toLowerCase())
    // When selected role is UPT then return only provinces
    return selectedRole === BCKS_UPT_PREFIX && isSuperAdminUser
      ? searched && region.type === RegionType.PROVINSI
      : searched
  }
  const optionsRegion: {
    parent: RegionDetail
    regions: RegionOption[]
  }[] = [
    {
      parent: REGION_EMPTY,
      regions: provinceData.filter(filterRegion),
    },
    ...regionData.map((r) => ({
      parent: r.province,
      regions: r.cities.filter(filterRegion).map((city) => ({
        parent: r.province,
        ...city,
      })),
    })),
  ]
  const flattenedOptionsRegion = optionsRegion.flatMap((r) => r.regions)

  const handleSwitchRole = async () => {
    if (
      isSuperAdminUser &&
      (selectedRole !== DEFAULT_ROLE ||
        [BCKS_REGION_PREFIX, BCKS_UPT_PREFIX].includes(selectedRole))
    ) {
      try {
        await impersonateGroup({
          group: MAP_ROLE_SWITCH[selectedRole],
          regionId: selectedRegion.id,
        })

        const changed = [
          ...(selectedRole !== DEFAULT_ROLE ? ['peran'] : []),
          ...(selectedRegion.id !== regionDetail.id ? ['wilayah'] : []),
        ].join(' dan ')
        toast({
          type: 'success',
          message: `Berhasil ubah ${changed}!`,
        })
        await getGroups()
        applySelectedRegion({
          id: selectedRegion.id,
          type: getRegionType(selectedRegion.id),
          name: selectedRegion.name,
        })
        setIsOpenDialogChangeRole(false)

        // Reset draft program pelatihan
        if (selectedRole === BCKS_UPT_PREFIX && !!program.name) {
          resetProgramDraft()
          location.reload()
        }
      } catch {
        toast({
          message: DEFAULT_ERROR_MESSAGE,
        })
      }
    } else {
      applySelectedRegion({
        id: selectedRegion.id,
        type: getRegionType(selectedRegion.id),
        name: selectedRegion.name,
      })
      toast({
        type: 'success',
        message: 'Berhasil ubah wilayah!',
      })
      setIsOpenDialogChangeRole(false)
    }
  }

  useEffect(() => {
    if (
      isSuperAdminUser &&
      selectedRole === BCKS_UPT_PREFIX &&
      getRegionType(selectedRegion.id) !== RegionType.PROVINSI
    ) {
      setSelectedRegion(REGION_EMPTY)
    } else {
      setSelectedRegion(regionDetail)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole])

  return (
    <>
      <div className="flex items-center gap-2 rounded border border-subdued px-2.5 py-2">
        <Icon>account_circle</Icon>

        <div>
          <Text
            variant="helper-bold"
            className="uppercase"
            data-testid="text-active-role"
          >
            {hasConflictingRoles ? '-' : activeRoleName}
          </Text>
          <Text
            variant="helper"
            color="subdued"
            className="uppercase"
            data-testid="text-active-region"
          >
            {regionDetail.name}
          </Text>
        </div>

        {(isSuperAdminUser || canChangeRegion) && (
          <div>
            <Button
              size="sm"
              color="white"
              onClick={() => setIsOpenDialogChangeRole(true)}
            >
              Ubah
            </Button>
          </div>
        )}
      </div>

      <Dialog
        size="md"
        isOpen={isOpenDialogChangeRole}
        onClose={() => setIsOpenDialogChangeRole(false)}
        closeOnOverlayClick={false}
        closeOnEscKeyDown={false}
        data-testid="dialog-change-role"
      >
        <DialogOverlay />
        <DialogContent>
          <DialogCloseButton />

          <div className="space-y-4 p-5">
            <div className="mb-8 space-y-1">
              <Text variant="heading-md">Pengaturan Peran dan Wilayah</Text>
              <Text variant="body-sm" color="subdued">
                Pilih peran dan wilayah yang sesuai dengan akses dan kewenangan
                Anda dalam sistem.
              </Text>
            </div>

            <Text variant="body-sm-bold">Pilih Peran</Text>
            {ROLE_DETAILS.filter(
              (role) => isSuperAdminUser || role.key === DEFAULT_ROLE
            ).map((role) => (
              <button
                key={role.key}
                className={clsx(
                  'flex w-full cursor-pointer items-center gap-4 rounded border p-3.5 text-left transition-all',
                  {
                    'border-subdued hover:bg-surface-subdued':
                      selectedRole !== role.key,
                    'border-blue-30 bg-surface-informational-subdued':
                      selectedRole === role.key,
                  }
                )}
                onClick={() => setSelectedRole(role.key)}
                data-testid={`button-role-${role.name}`}
              >
                <Icon
                  className="justify-end"
                  color={
                    selectedRole === role.key ? 'informational' : 'default'
                  }
                >
                  account_circle
                </Icon>

                <div className="flex-1">
                  <Text
                    variant="body-sm-bold"
                    color={selectedRole === role.key ? 'primary' : 'default'}
                    className="uppercase"
                  >
                    {role.name}
                  </Text>
                  <Text
                    variant="body-sm"
                    color={selectedRole === role.key ? 'primary' : 'subdued'}
                  >
                    {role.description}
                  </Text>
                </div>

                {selectedRole === role.key && (
                  <Icon
                    color="informational"
                    fontSize="sm"
                    className="justify-end"
                  >
                    check
                  </Icon>
                )}
              </button>
            ))}

            <Text variant="body-sm-bold">Pilih Wilayah</Text>

            {selectedRole === BCKS_UPT_PREFIX && (
              <div className="rounded bg-surface-informational-subdued p-2.5">
                <Text variant="body-sm" color="primary">
                  <strong>Info:</strong> Peran UPT hanya dapat memilih wilayah
                  provinsi
                </Text>
              </div>
            )}

            <Popover trigger="click" placement="top-start" nested>
              <PopoverTrigger className="flex w-full items-center justify-between rounded border border-subdued px-2.5 py-2">
                <div
                  ref={refInputSelectedRegion}
                  className="flex items-center gap-2"
                  data-testid="input-region"
                >
                  <Icon fontSize="sm">location_on</Icon>
                  <Text variant="body-sm-bold" className="uppercase">
                    {selectedRegion?.name || '-'}
                  </Text>
                </div>

                <Icon fontSize="sm">keyboard_arrow_down</Icon>
              </PopoverTrigger>

              <PopoverContent className="w-[680px]">
                <div className="-mx-2 -my-1">
                  <div className="border-b border-subdued p-3">
                    <Text variant="body-sm-bold" className="mb-2">
                      Pilih Wilayah
                    </Text>
                    <InputSearch
                      placeholder="Cari provinsi, kabupaten, atau kota..."
                      onChange={setSearchRegion}
                    />
                  </div>

                  <div className="max-h-80 overflow-auto">
                    {flattenedOptionsRegion.length === 0 ? (
                      <>
                        <div className="space-y-1 p-4 text-center">
                          <Text variant="body-sm" color="subdued">
                            Tidak ada wilayah yang ditemukan
                          </Text>
                          <Text variant="helper" color="subdued">
                            Coba gunakan kata kunci yang berbeda
                          </Text>
                        </div>
                      </>
                    ) : (
                      optionsRegion.map((option) => (
                        <React.Fragment key={option.parent.id}>
                          {option.parent.name && !!option.regions.length && (
                            <div className="mt-2 bg-surface-subdued px-3.5 py-2">
                              <Text
                                variant="helper"
                                color="subdued"
                                className="uppercase"
                              >
                                {option.parent.name}
                              </Text>
                            </div>
                          )}

                          {option.regions.map((region) => (
                            <button
                              key={region.id}
                              className={clsx(
                                'flex w-full cursor-pointer items-center gap-3 border-t border-gray-10 bg-white p-2.5 text-start hover:bg-surface-subdued',
                                {
                                  'bg-surface-subdued':
                                    selectedRegion?.id === region.id,
                                }
                              )}
                              data-testid={`button-region-${region.name}`}
                              onClick={() => {
                                setSelectedRegion(region)
                                refInputSelectedRegion.current?.click()
                              }}
                            >
                              <Icon fontSize="sm">location_on</Icon>

                              <div className="flex-1 space-y-1">
                                <Text
                                  variant="body-sm-bold"
                                  className="uppercase"
                                >
                                  {region.name}
                                </Text>
                                {getRegionType(region.id) ===
                                  RegionType.PROVINSI && (
                                  <CustomBadge color="blue">
                                    Provinsi
                                  </CustomBadge>
                                )}
                                {getRegionType(region.id) ===
                                  RegionType.KABUPATEN &&
                                  region.name.startsWith('Kota') && (
                                    <CustomBadge color="green">
                                      Kota
                                    </CustomBadge>
                                  )}
                                {getRegionType(region.id) ===
                                  RegionType.KABUPATEN &&
                                  region.name.startsWith('Kab.') && (
                                    <CustomBadge color="purple">
                                      Kabupaten
                                    </CustomBadge>
                                  )}
                                <Text
                                  variant="helper"
                                  color="subdued"
                                  className="uppercase"
                                >
                                  {region.parent?.name}
                                </Text>
                              </div>

                              {selectedRegion?.id === region.id && (
                                <Icon fontSize="sm" color="informational">
                                  check
                                </Icon>
                              )}
                            </button>
                          ))}
                        </React.Fragment>
                      ))
                    )}
                  </div>

                  <div className="border-t border-subdued bg-default p-3">
                    <div className="flex items-center gap-2">
                      <Icon className="!size-[12px] !text-[12px] !text-subdued">
                        location_on
                      </Icon>
                      <Text variant="helper" color="subdued">
                        Total: {flattenedOptionsRegion.length} wilayah
                      </Text>
                    </div>
                  </div>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          <DialogFooter>
            <div className="flex justify-end gap-3">
              <Button
                color="white"
                onClick={() => setIsOpenDialogChangeRole(false)}
              >
                Batal
              </Button>
              <Button
                disabled={
                  !selectedRole ||
                  !selectedRegion?.id ||
                  isLoadingImpersonateGroup
                }
                isLoading={isLoadingImpersonateGroup}
                onClick={handleSwitchRole}
              >
                Simpan
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default RoleAndRegionDetails
